import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/erp";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Stethoscope, ArrowRight, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login, requestOtp, loginWithOtp } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mode, setMode] = useState("otp");
  const [sent, setSent] = useState(false);
  const [code, setCode] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!cooldown) return undefined;
    const timer = setTimeout(() => setCooldown((remaining) => remaining - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const sendCode = async () => {
    setError(""); setLoading(true);
    try {
      await requestOtp(email);
      setSent(true);
      setCode("");
      setCooldown(60);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "otp" && !sent) return sendCode();
    setError(""); setLoading(true);
    try {
      if (mode === "otp") await loginWithOtp(email, code);
      else await login(email, password);
      toast.success("Welcome back!");
      navigate("/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex flex-col justify-between w-1/2 bg-brand p-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10"
          style={{ backgroundImage: "url(https://images.unsplash.com/photo-1580281657527-47f249e8f4df?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200)", backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="absolute inset-0 bg-white/20" />
        <div className="relative flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-white grid place-items-center shadow-sm">
            <Stethoscope className="h-6 w-6 text-brand-600" />
          </div>
          <div>
            <div className="font-heading text-xl font-bold text-slate-900">VGRUPS Health Care</div>
            <div className="text-xs uppercase tracking-widest text-slate-700">GST Accounting ERP</div>
          </div>
        </div>
        <div className="relative">
          <h1 className="font-heading text-4xl font-bold text-slate-900 leading-tight">
            Pharma-grade billing,<br />GST compliance & inventory.
          </h1>
          <p className="text-slate-700 mt-4 max-w-md">
            Batch & expiry tracking, CGST/SGST/IGST invoicing, A4 & thermal prints, payments and
            real-time dashboards — built for medical distribution.
          </p>
          <div className="flex gap-6 mt-8 text-slate-800 text-sm">
            <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4" /> GST Ready</div>
            <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4" /> Batch/Expiry</div>
            <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4" /> A4 / Thermal</div>
          </div>
        </div>
        <div className="relative text-xs text-slate-700">© {new Date().getFullYear()} VGRUPS Health Care Pvt Ltd</div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 bg-white">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2.5 mb-8">
            <div className="h-10 w-10 rounded-lg bg-brand grid place-items-center">
              <Stethoscope className="h-5 w-5 text-brand-foreground" />
            </div>
            <span className="font-heading font-bold text-lg">VGRUPS ERP</span>
          </div>
          <h2 className="font-heading text-2xl font-bold">Sign in</h2>
          <p className="text-sm text-muted-foreground mt-1">Access your accounting workspace</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} data-testid="login-email-input" autoComplete="email"
                onChange={(e) => setEmail(e.target.value)} className="mt-1.5" required disabled={mode === "otp" && sent} />
            </div>
            {mode === "password" ? (
              <div>
                <Label htmlFor="password">Password</Label>
                <Input id="password" type="password" value={password} data-testid="login-password-input"
                  onChange={(e) => setPassword(e.target.value)} className="mt-1.5" autoComplete="current-password" required />
              </div>
            ) : sent ? (
              <>
                <p className="text-sm text-muted-foreground" role="status">
                  If this email has an account, a code has been sent. Check your inbox and spam folder. The code expires in 10 minutes.
                </p>
                <div>
                  <Label htmlFor="code">6-digit email code</Label>
                  <Input id="code" value={code} data-testid="login-code-input" className="mt-1.5 font-mono tracking-widest"
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    autoComplete="one-time-code" inputMode="numeric" pattern="[0-9]{6}" maxLength={6} required />
                </div>
                <div className="flex justify-between text-sm">
                  <button type="button" className="text-brand-700 hover:underline" onClick={() => {
                    setSent(false); setCode(""); setError("");
                  }}>Change email</button>
                  <button type="button" className="text-brand-700 hover:underline disabled:text-slate-400"
                    onClick={sendCode} disabled={loading || cooldown > 0}>
                    {cooldown ? `Resend in ${cooldown}s` : "Resend code"}
                  </button>
                </div>
              </>
            ) : (
              <p className="text-sm text-muted-foreground">We’ll email a one-time code to an existing account. No password needed.</p>
            )}
            {error && <div className="text-sm text-rose-600" role="alert" data-testid="login-error">{error}</div>}
            <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit-button">
              {loading ? "Please wait…" : mode === "password" ? "Sign In" : sent ? "Verify code & sign in" : "Email me a code"}
              <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </form>
          <button type="button" className="w-full mt-4 text-sm text-brand-700 hover:underline"
            data-testid="login-mode-toggle" onClick={() => {
              setMode((current) => current === "password" ? "otp" : "password");
              setSent(false); setCode(""); setError("");
            }}>
            {mode === "password" ? "Sign in with email code instead" : "Sign in with password instead"}
          </button>
        </div>
      </div>
    </div>
  );
}
